/** @jsxImportSource react */
'use client';

import { motion } from 'motion/react';

import { Dove } from './brand';
import { ArrowRight } from './icons';
import { SignupLink } from './signup-link';
import { TrainingLink } from './training-link';

const EASE = [0.22, 1, 0.36, 1] as const;

// Fade-up on scroll-into-view, shared by the headline, blurb and button row.
function reveal(y: number, delay = 0) {
  return {
    initial: { opacity: 0, y },
    transition: { delay, duration: 0.6, ease: EASE },
    viewport: { margin: '-100px', once: true },
    whileInView: { opacity: 1, y: 0 },
  };
}

export function FinalCta() {
  return (
    <section className="relative overflow-hidden bg-white">
      <motion.div
        animate={{ rotate: [0, -1.5, 0, 1.5, 0], y: [0, 8, 0, -8, 0] }}
        className="pointer-events-none absolute -top-[100px] -right-[160px] z-0"
        transition={{ duration: 28, ease: 'easeInOut', repeat: Infinity }}
      >
        <Dove aria-hidden className="h-[610px] w-[700px] opacity-[0.04]" />
      </motion.div>
      <div className="relative z-10 mx-auto flex max-w-[1440px] flex-col items-center gap-8 px-5 py-12 pb-20 sm:px-8 sm:py-16 sm:pb-28 lg:gap-12 lg:px-20 lg:py-[60px] lg:pb-[180px]">
        <motion.div
          className="flex flex-col items-center gap-1 text-center"
          {...reveal(16)}
        >
          <h2 className="text-[clamp(56px,9vw,120px)] font-bold tracking-[-0.04em] [line-height:1.05] text-[color:var(--color-ink)]">
            Build your first class
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5">
            <span className="text-[clamp(56px,9vw,120px)] font-bold tracking-[-0.04em] [line-height:1.05] text-[color:var(--color-ink)]">
              in
            </span>
            <span className="relative inline-block text-[clamp(56px,9vw,120px)] font-bold tracking-[-0.04em] [line-height:1.05] text-[color:var(--color-ink)]">
              {/* Highlighter draws in from left when scrolled into view */}
              <motion.span
                aria-hidden
                className="absolute inset-0 -z-[1] bg-[color:var(--color-highlight)]"
                initial={{ scaleX: 0, transformOrigin: 'left center' }}
                style={{
                  bottom: '0.05em',
                  left: '-0.25em',
                  right: '-0.25em',
                  top: '0.05em',
                }}
                transition={{
                  delay: 0.3,
                  duration: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ margin: '-100px', once: true }}
                whileInView={{ scaleX: 1 }}
              />
              an hour.
            </span>
          </div>
        </motion.div>

        <motion.p
          className="max-w-[560px] pt-2 text-center text-[20px] leading-[1.6] text-[color:var(--color-gray-dark)]"
          {...reveal(12, 0.1)}
        >
          Start free for 21 days. Build and publish your first class, welcome
          real guests — $0 today, card up front. Cancel anytime before day 21.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-5 pt-2"
          {...reveal(12, 0.15)}
        >
          <SignupLink
            className="group inline-flex items-center gap-3 rounded-[14px] bg-[color:var(--color-amethyst)] px-11 py-6 text-[18px] font-semibold text-white shadow-[0_16px_40px_-10px_rgba(123,44,255,0.4)] transition-all hover:-translate-y-0.5 hover:shadow-[0_22px_50px_-12px_rgba(123,44,255,0.55)]"
            source="final-cta"
          >
            <span>Try OnArk free</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </SignupLink>
          <TrainingLink
            className="link-underline text-[18px] font-semibold text-[color:var(--color-ink)] transition-colors hover:text-[color:var(--color-amethyst)]"
            source="home_final_cta"
          >
            Or see how it works →
          </TrainingLink>
        </motion.div>
      </div>
    </section>
  );
}
